// Mon Blog - Page avec éditeur complet et planification
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Plus, Edit, Trash2, Eye, Heart, MessageCircle, 
  Calendar, Clock, Tag, Search, Filter, MoreHorizontal,
  PenTool, BookOpen, TrendingUp, X, Upload, Image, Link,
  Bold, Italic, List, ListOrdered, Quote, Code, Save, Send,
  ChevronDown, Globe, Lock, Hash, CalendarClock, Timer, ChevronRight, CheckCircle,
  Users
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// LISTE DES MÉTIERS (identique à CreatePublicationModal)
// ══════════════════════════════════════════════════════════════════════════════
const METIERS_LIST = [
  { value: 'pisciniste', label: 'Pisciniste' },
  { value: 'plombier', label: 'Plombier' },
  { value: 'electricien', label: 'Électricien' },
  { value: 'macon', label: 'Maçon' },
  { value: 'carreleur', label: 'Carreleur' },
  { value: 'peintre', label: 'Peintre' },
  { value: 'menuisier', label: 'Menuisier' },
  { value: 'couvreur', label: 'Couvreur' },
  { value: 'chauffagiste', label: 'Chauffagiste' },
  { value: 'paysagiste', label: 'Paysagiste' },
  { value: 'climaticien', label: 'Climaticien' },
  { value: 'jardinier', label: 'Jardinier' },
  { value: 'facadier', label: 'Façadier' },
  { value: 'serrurier', label: 'Serrurier' },
  { value: 'charpentier', label: 'Charpentier' },
  { value: 'plaquiste', label: 'Plaquiste' },
  { value: 'cuisiniste', label: 'Cuisiniste' },
  { value: 'architecte', label: 'Architecte' },
];

const DEMO_ARTICLES = [];

// Composant Éditeur d'article
function ArticleEditor({ isOpen, onClose, article, onSave }) {
  const [title, setTitle] = useState(article?.title || '');
  const [excerpt, setExcerpt] = useState(article?.excerpt || '');
  const [content, setContent] = useState(article?.content || '');
  const [coverImage, setCoverImage] = useState(article?.coverImage || article?.cover_image || null);
  const [selectedMetier, setSelectedMetier] = useState(article?.metier || '');
  const [tags, setTags] = useState(article?.tags?.join(', ') || '');
  const [status, setStatus] = useState(article?.status || 'draft');
  const [showSEO, setShowSEO] = useState(false);
  const [seoTitle, setSeoTitle] = useState(article?.seoTitle || article?.seo_title || '');
  const [seoDescription, setSeoDescription] = useState(article?.seoDescription || article?.seo_description || '');
  const [saving, setSaving] = useState(false);
  const [visibility, setVisibility] = useState(article?.visibility || 'public');
  
  // Scheduling state
  const [showScheduling, setShowScheduling] = useState(article?.status === 'scheduled');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  
  const fileInputRef = useRef(null);
  const contentRef = useRef(null);

  // Initialize scheduled date/time from article
  useEffect(() => {
    if (article?.scheduled_at) {
      try {
        const date = new Date(article.scheduled_at);
        setScheduledDate(date.toISOString().split('T')[0]);
        setScheduledTime(date.toTimeString().slice(0, 5));
        setShowScheduling(true);
      } catch (e) {
        console.error('Error parsing scheduled_at:', e);
      }
    }
  }, [article]);

  useEffect(() => {
    if (article) {
      setTitle(article.title || '');
      setExcerpt(article.excerpt || '');
      setContent(article.content || '');
      setCoverImage(article.coverImage || article.cover_image || null);
      setSelectedMetier(article.metier || '');
      setTags(article.tags?.join(', ') || '');
      setStatus(article.status || 'draft');
      setSeoTitle(article.seoTitle || article.seo_title || '');
      setSeoDescription(article.seoDescription || article.seo_description || '');
      setVisibility(article.visibility || 'public');
    }
  }, [article]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Sélectionne un fichier image');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image trop lourde (max 5 Mo)');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => setCoverImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const insertFormat = (format) => {
    const textarea = contentRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    let newText = '';
    switch (format) {
      case 'bold':
        newText = `**${selectedText || 'texte en gras'}**`;
        break;
      case 'italic':
        newText = `*${selectedText || 'texte en italique'}*`;
        break;
      case 'list':
        newText = `\n- ${selectedText || 'élément de liste'}`;
        break;
      case 'ordered':
        newText = `\n1. ${selectedText || 'élément numéroté'}`;
        break;
      case 'quote':
        newText = `\n> ${selectedText || 'citation'}`;
        break;
      case 'code':
        newText = `\`${selectedText || 'code'}\``;
        break;
      case 'link':
        newText = `[${selectedText || 'texte du lien'}](url)`;
        break;
      case 'image':
        newText = `\n![description](url-de-image)\n`;
        break;
      default:
        return;
    }
    
    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + newText.length, start + newText.length);
    }, 0);
  };

  const handleSave = async (publishNow = false, scheduleIt = false) => {
    if (!title.trim()) {
      toast.error('Donne un titre à ton article');
      return;
    }
    if (!content.trim()) {
      toast.error('Écris du contenu pour ton article');
      return;
    }
    if (!selectedMetier) {
      toast.error('Choisis un métier pour ta publication');
      return;
    }
    
    // Validate scheduling
    if (scheduleIt) {
      if (!scheduledDate) {
        toast.error('Choisis une date de publication');
        return;
      }
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error('La date de publication doit être dans le futur');
        return;
      }
    }

    setSaving(true);
    
    // Determine status
    let finalStatus = status;
    let scheduledAt = null;
    
    if (publishNow) {
      finalStatus = 'published';
    } else if (scheduleIt && scheduledDate) {
      finalStatus = 'scheduled';
      scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    }
    
    const articleData = {
      id: article?.id || Date.now(),
      title,
      excerpt: excerpt || content.substring(0, 150) + '...',
      content,
      coverImage,
      cover_image: coverImage,
      metier: selectedMetier,
      tags: tags.split(',').map(t => t.trim()).filter(t => t),
      status: finalStatus,
      visibility: visibility,
      scheduled_at: scheduledAt,
      seoTitle: seoTitle || title,
      seo_title: seoTitle || title,
      seoDescription: seoDescription || excerpt || content.substring(0, 160),
      seo_description: seoDescription || excerpt || content.substring(0, 160),
      publishedAt: publishNow ? new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : article?.publishedAt,
      views: article?.views || 0,
      likes: article?.likes || 0,
      comments: article?.comments || 0,
      readTime: Math.ceil(content.split(' ').length / 200) || 1,
    };

    // Simuler sauvegarde (à connecter à l'API)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    onSave(articleData);
    setSaving(false);
    
    if (publishNow) {
      toast.success('Article publié !');
    } else if (scheduleIt) {
      const dateStr = new Date(`${scheduledDate}T${scheduledTime}`).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      toast.success(`Article programmé pour le ${dateStr}`);
    } else {
      toast.success('Brouillon enregistré');
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
          <div>
            <h1 className="font-semibold text-gray-900">
              {article?.id ? 'Modifier l\'article' : 'Nouvel article'}
            </h1>
            <p className="text-sm text-gray-500">
              {status === 'draft' ? 'Brouillon' : 'Publié'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => handleSave(false, false)}
            disabled={saving}
          >
            <Save size={16} className="mr-2" />
            Enregistrer
          </Button>
          <div className="relative">
            <Button 
              variant="outline"
              onClick={() => setShowScheduling(!showScheduling)}
              disabled={saving}
              className={showScheduling ? 'border-purple-500 text-purple-600' : ''}
            >
              <CalendarClock size={16} className="mr-2" />
              Programmer
            </Button>
            {/* Scheduling Popup */}
            <AnimatePresence>
              {showScheduling && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Timer size={18} className="text-purple-500" />
                    <h4 className="font-semibold text-gray-900">Planifier la publication</h4>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="schedule-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Heure</label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="schedule-time"
                      />
                    </div>
                    <Button 
                      onClick={() => handleSave(false, true)}
                      disabled={saving || !scheduledDate}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="confirm-schedule-btn"
                    >
                      <CalendarClock size={16} className="mr-2" />
                      {saving ? 'Programmation...' : 'Confirmer la programmation'}
                    </Button>
                  </div>
                  {scheduledDate && (
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Publication prévue le {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Button 
            onClick={() => handleSave(true, false)}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Send size={16} className="mr-2" />
            {saving ? 'Publication...' : 'Publier maintenant'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          {/* Cover Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image de couverture</label>
            {coverImage ? (
              <div className="relative aspect-[2/1] rounded-xl overflow-hidden border border-gray-200">
                <img src={coverImage} alt="Couverture" className="w-full h-full object-cover" />
                <button 
                  onClick={() => setCoverImage(null)}
                  className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white shadow-md"
                >
                  <X size={16} className="text-gray-700" />
                </button>
              </div>
            ) : (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="aspect-[2/1] border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors"
              >
                <Upload size={32} className="text-gray-400 mb-2" />
                <p className="text-gray-600">Clique pour ajouter une image de couverture</p>
                <p className="text-sm text-gray-400 mt-1">Recommandé : 1200 × 600 pixels</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre de l'article *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Un titre accrocheur pour ton article..."
              className="w-full px-4 py-3 text-xl font-semibold border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="article-title"
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Résumé / Accroche</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Un court résumé qui donne envie de lire..."
              rows={2}
              maxLength={200}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{excerpt.length}/200</p>
          </div>

          {/* Catégorie (Métier) - Filtres horizontaux comme sur mobile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie (Métier) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div 
                className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 pr-8"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {METIERS_LIST.map((metier) => (
                  <button
                    key={metier.value}
                    onClick={() => setSelectedMetier(metier.value)}
                    className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                      selectedMetier === metier.value
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {metier.label}
                  </button>
                ))}
              </div>
              {/* Indicateur de scroll */}
              <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end pr-1">
                <ChevronRight size={16} className="text-orange-500 animate-pulse" />
              </div>
            </div>
            {!selectedMetier && (
              <p className="text-xs text-red-500 mt-1">Choisis un métier pour ta publication</p>
            )}
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contenu de l'article *</label>
            
            {/* Toolbar */}
            <div className="flex items-center gap-1 p-2 bg-gray-50 border border-gray-200 rounded-t-xl border-b-0">
              <button onClick={() => insertFormat('bold')} className="p-2 hover:bg-gray-200 rounded" title="Gras">
                <Bold size={18} className="text-gray-600" />
              </button>
              <button onClick={() => insertFormat('italic')} className="p-2 hover:bg-gray-200 rounded" title="Italique">
                <Italic size={18} className="text-gray-600" />
              </button>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <button onClick={() => insertFormat('list')} className="p-2 hover:bg-gray-200 rounded" title="Liste">
                <List size={18} className="text-gray-600" />
              </button>
              <button onClick={() => insertFormat('ordered')} className="p-2 hover:bg-gray-200 rounded" title="Liste numérotée">
                <ListOrdered size={18} className="text-gray-600" />
              </button>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <button onClick={() => insertFormat('quote')} className="p-2 hover:bg-gray-200 rounded" title="Citation">
                <Quote size={18} className="text-gray-600" />
              </button>
              <button onClick={() => insertFormat('code')} className="p-2 hover:bg-gray-200 rounded" title="Code">
                <Code size={18} className="text-gray-600" />
              </button>
              <div className="w-px h-6 bg-gray-300 mx-1" />
              <button onClick={() => insertFormat('link')} className="p-2 hover:bg-gray-200 rounded" title="Lien">
                <Link size={18} className="text-gray-600" />
              </button>
              <button onClick={() => insertFormat('image')} className="p-2 hover:bg-gray-200 rounded" title="Image">
                <Image size={18} className="text-gray-600" />
              </button>
            </div>
            
            <textarea
              ref={contentRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Écris ton article ici...

Tu peux utiliser la syntaxe Markdown :
- **texte en gras**
- *texte en italique*
- [lien](url)
- > citation
- - liste à puces
- 1. liste numérotée"
              rows={15}
              className="w-full px-4 py-3 border border-gray-200 rounded-b-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none font-mono text-sm"
              data-testid="article-content"
            />
            <p className="text-xs text-gray-400 text-right mt-1">
              {content.split(' ').filter(w => w).length} mots • ~{Math.ceil(content.split(' ').length / 200) || 1} min de lecture
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Hash size={16} className="inline mr-1" />
              Tags (séparés par des virgules)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="bricolage, tutoriel, débutant..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* SEO Section */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowSEO(!showSEO)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Globe size={18} className="text-gray-500" />
                <span className="font-medium text-gray-700">Référencement SEO</span>
              </div>
              <ChevronDown size={18} className={`text-gray-400 transition-transform ${showSEO ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {showSEO && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titre SEO</label>
                      <input
                        type="text"
                        value={seoTitle}
                        onChange={(e) => setSeoTitle(e.target.value)}
                        placeholder={title || 'Titre pour les moteurs de recherche'}
                        maxLength={60}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                      <p className="text-xs text-gray-400 text-right mt-1">{seoTitle.length}/60</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description SEO</label>
                      <textarea
                        value={seoDescription}
                        onChange={(e) => setSeoDescription(e.target.value)}
                        placeholder={excerpt || 'Description pour les moteurs de recherche'}
                        maxLength={160}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right mt-1">{seoDescription.length}/160</p>
                    </div>
                    
                    {/* Aperçu Google */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-2">Aperçu dans Google :</p>
                      <div className="bg-white rounded border border-gray-200 p-3">
                        <p className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                          {seoTitle || title || 'Titre de l\'article'}
                        </p>
                        <p className="text-green-700 text-sm">abracadabati.com › blog › article</p>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {seoDescription || excerpt || 'Description de l\'article qui apparaîtra dans les résultats de recherche...'}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Visibilité - Style identique au mobile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
            <div className="flex gap-2">
              {[
                { id: 'public', label: 'Public', icon: Globe },
                { id: 'followers', label: 'Abonnés', icon: Lock },
                { id: 'private', label: 'Privé', icon: Lock },
              ].map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setVisibility(opt.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                      visibility === opt.id
                        ? 'bg-orange-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon size={18} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArticleCard({ article, onEdit, onDelete, isDemo }) {
  const isDraft = article.status === 'draft';
  const isScheduled = article.status === 'scheduled';
  const isPublished = article.status === 'published';

  // Format scheduled date
  const getScheduledDateDisplay = () => {
    if (!article.scheduled_at) return null;
    try {
      return new Date(article.scheduled_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
    >
      {/* Cover Image */}
      <div className="relative h-48 overflow-hidden">
        {(article.coverImage || article.cover_image) ? (
          <img 
            src={article.coverImage || article.cover_image} 
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <FileText size={48} className="text-gray-300" />
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center gap-1 ${
            isScheduled 
              ? 'bg-purple-100 text-purple-700' 
              : isDraft 
                ? 'bg-yellow-100 text-yellow-700' 
                : 'bg-green-100 text-green-700'
          }`}>
            {isScheduled && <CalendarClock size={12} />}
            {isScheduled ? 'Programmé' : isDraft ? 'Brouillon' : 'Publié'}
          </span>
        </div>
        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={() => onEdit(article)}
            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
          >
            <Edit size={16} className="text-gray-700" />
          </button>
          <button 
            onClick={() => onDelete(article.id)}
            className="p-2 bg-white rounded-full shadow-md hover:bg-red-50"
          >
            <Trash2 size={16} className="text-red-500" />
          </button>
        </div>
        
        {/* Scheduled banner */}
        {isScheduled && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-purple-900/80 to-transparent p-3">
            <p className="text-white text-sm flex items-center gap-2">
              <Timer size={14} />
              Publication le {getScheduledDateDisplay()}
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#1E3A5F] transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-2 mb-4">{article.excerpt}</p>

        {/* Categories & Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {article.metier && (
            <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">
              {METIERS_LIST.find(m => m.value === article.metier)?.label || article.metier}
            </span>
          )}
          {article.tags?.slice(0, 2).map(tag => (
            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
              #{tag}
            </span>
          ))}
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            {(article.publishedAt || article.published_at) && (
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {article.publishedAt || new Date(article.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {article.readTime} min
            </span>
          </div>
          {!isDraft && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Eye size={12} /> {article.views}
              </span>
              <span className="flex items-center gap-1">
                <Heart size={12} /> {article.likes}
              </span>
              <span className="flex items-center gap-1">
                <MessageCircle size={12} /> {article.comments}
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function BlogPage() {
  const { user } = useCurrentUser();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  
  // Refs et états pour le scroll des filtres mobile
  const scrollContainerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Gérer le scroll des filtres
  const handleScrollFilters = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const isDemo = user?.isDemo;

  // Charger les articles depuis l'API
  const fetchArticles = async () => {
    if (isDemo) {
      setArticles(DEMO_ARTICLES);
      setLoading(false);
      return;
    }
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/blog/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setArticles(data.posts || []);
      }
    } catch (err) {
      console.error('Erreur chargement articles:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user !== null) {
      fetchArticles();
    }
  }, [user, isDemo]);

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filter === 'all' || 
                          (filter === 'published' && article.status === 'published') ||
                          (filter === 'draft' && article.status === 'draft') ||
                          (filter === 'scheduled' && article.status === 'scheduled');
    const matchesCategory = categoryFilter === 'all' || article.metier === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const publishedCount = articles.filter(a => a.status === 'published').length;
  const draftCount = articles.filter(a => a.status === 'draft').length;
  const scheduledCount = articles.filter(a => a.status === 'scheduled').length;
  const totalViews = articles.reduce((acc, a) => acc + (a.views || 0), 0);

  const handleCreateArticle = () => {
    if (isDemo) {
      toast.error('Crée ton compte pour écrire des articles !');
      return;
    }
    setEditingArticle(null);
    setShowEditor(true);
  };

  const handleEdit = (article) => {
    if (isDemo) {
      toast.error('Crée ton compte pour modifier cet article !');
      return;
    }
    setEditingArticle(article);
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (isDemo) {
      toast.error('Crée ton compte pour supprimer cet article !');
      return;
    }
    if (!confirm('Supprimer cet article ?')) return;
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/ecosystem/blog/posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setArticles(prev => prev.filter(a => a.id !== id));
        toast.success('Article supprimé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      toast.error('Erreur réseau');
    }
  };

  const handleSaveArticle = async (articleData) => {
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('token');
    if (!token) return;
    
    try {
      const isEdit = !!editingArticle?.id;
      const url = isEdit 
        ? `${API_URL}/api/ecosystem/blog/posts/${editingArticle.id}`
        : `${API_URL}/api/ecosystem/blog/posts`;
      
      const body = {
        title: articleData.title,
        excerpt: articleData.excerpt,
        content: articleData.content,
        cover_image: articleData.coverImage || articleData.cover_image,
        metier: articleData.metier,
        tags: articleData.tags,
        status: articleData.status,
        visibility: articleData.visibility,
        scheduled_at: articleData.scheduled_at,
        seo_title: articleData.seoTitle || articleData.seo_title,
        seo_description: articleData.seoDescription || articleData.seo_description
      };
      
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        // Rafraîchir la liste
        fetchArticles();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde article:', err);
      toast.error('Erreur réseau');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Editor Modal */}
      <ArticleEditor
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        article={editingArticle}
        onSave={handleSaveArticle}
      />

      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mon Blog</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Partage tes connaissances
          </p>
        </div>
        <Button 
          onClick={handleCreateArticle}
          className="w-full sm:w-auto bg-[#1E3A5F] hover:bg-[#1E3A5F]/90 text-white py-3 sm:py-2"
          data-testid="new-article-btn"
        >
          <Plus size={18} className="mr-2" />
          Nouvel article
        </Button>
      </div>

      {/* Banner démo */}
      {isDemo && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 flex items-start sm:items-center gap-3">
          <PenTool size={20} className="text-amber-500 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div>
            <p className="font-medium text-amber-800 text-sm sm:text-base">Mode Démonstration</p>
            <p className="text-xs sm:text-sm text-amber-700">Ces articles sont des exemples. Crée ton compte pour publier.</p>
          </div>
        </div>
      )}

      {/* Stats - Mobile Grid */}
      {articles.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BookOpen size={18} className="text-green-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{publishedCount}</p>
                <p className="text-xs sm:text-sm text-gray-500">Publiés</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <CalendarClock size={18} className="text-purple-600" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{scheduledCount}</p>
                <p className="text-xs sm:text-sm text-gray-500">Programmés</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileText size={18} className="text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{draftCount}</p>
                <p className="text-sm text-gray-500">Brouillons</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalViews.toLocaleString()}</p>
                <p className="text-sm text-gray-500">Vues totales</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Mobile Optimized */}
      {articles.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          {/* Recherche */}
          <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 border border-gray-200 rounded-lg">
            <Search size={14} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-0 outline-none text-xs text-gray-700 placeholder-gray-400"
            />
          </div>
          
          {/* Filter Pills Mobile */}
          <div className="sm:hidden">
            <div className="relative flex items-center">
              <div 
                ref={scrollContainerRef}
                onScroll={handleScrollFilters}
                className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {[
                  { id: 'all', label: 'Tous', icon: FileText },
                  { id: 'published', label: 'Publiés', icon: CheckCircle },
                  { id: 'scheduled', label: 'Programmés', icon: CalendarClock },
                  { id: 'draft', label: 'Brouillons', icon: Clock },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    data-testid={`filter-${f.id}`}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      filter === f.id 
                        ? 'bg-orange-500 text-white shadow-sm' 
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <f.icon size={12} />
                    {f.label}
                  </button>
                ))}
              </div>
              
              {/* Flèche orange discrète */}
              {showScrollHint && (
                <div className="flex-shrink-0 flex items-center pointer-events-none">
                  <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
                </div>
              )}
            </div>
          </div>

          {/* Filter Pills Desktop */}
          <div className="hidden sm:flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Tous', icon: FileText },
              { id: 'published', label: 'Publiés', icon: CheckCircle },
              { id: 'scheduled', label: 'Programmés', icon: CalendarClock },
              { id: 'draft', label: 'Brouillons', icon: Clock },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === f.id 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <f.icon size={14} />
                {f.label}
              </button>
            ))}
            
            {/* Category Filter - Métiers */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 bg-white"
            >
              <option value="all">Métier</option>
              {METIERS_LIST.map(metier => (
                <option key={metier.value} value={metier.value}>{metier.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* État vide - Mobile Optimized */}
      {!isDemo && articles.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 sm:p-12 text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-indigo-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <PenTool size={28} className="text-indigo-500" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Ton blog est vide</h3>
          <p className="text-sm sm:text-base text-gray-500 mb-6 max-w-md mx-auto">
            Partage tes connaissances avec la communauté !
          </p>
          <Button 
            onClick={handleCreateArticle}
            className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white py-3 sm:py-2"
          >
            <PenTool size={18} className="mr-2" />
            Écrire mon premier article
          </Button>
        </div>
      )}

      {/* Liste des articles - Mobile Responsive Grid */}
      {filteredArticles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredArticles.map(article => (
            <ArticleCard 
              key={article.id} 
              article={article}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isDemo={isDemo}
            />
          ))}
        </div>
      )}

      {articles.length > 0 && filteredArticles.length === 0 && (
        <div className="text-center py-8 sm:py-12 text-gray-500">
          <FileText size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Aucun article trouvé</p>
        </div>
      )}
    </div>
  );
}
